import { IManagedSite, IManagedSiteCollection } from "../src/taskpane/interfaces/IManagedSite";
import { ISiteInfo, ISiteInfoLocation } from "../src/taskpane/interfaces/ISiteInfo";

export class SharePointSiteHelper {
    public static hubSites: ISiteInfo[] = [];
    public static managedSites: IManagedSiteCollection | undefined;
    public static provisionedSites: string[] | undefined;
    // Global inactive-site indicator set sourced from the tenant client list (list titled "Clients").
    public static inactiveClientSites: IManagedSite[] = [];
    // Distinguishes "not fetched yet" from "fetched and empty" so surfaces that render before the
    // sites list loads (e.g. search results) can lazily fetch the set exactly once.
    public static inactiveClientSitesLoaded: boolean = false;
    // Whether the last read of the set was complete: a failed read still marks it loaded for the
    // views (which keep what they got), but Copilot reads it again until one is (review 2026-10-20).
    public static inactiveClientSitesComplete: boolean = false;
    // How many times this page ran reset(): the hosts that keep the page through a sign-out run it then (App.tsx and
    // contentManagerTeams resetAccount). A Sites or Teams load that started before one belongs to a sign-in that has
    // ended, even when the same account signed in again (2026-10-29).
    public static resets: number = 0;

    static reset(): void {
        this.resets++;
        this.hubSites = [];
        this.managedSites = undefined;
        this.provisionedSites = undefined;
        this.inactiveClientSites = [];
        this.inactiveClientSitesLoaded = false;
        this.inactiveClientSitesComplete = false;
    }

    static clearHubs(): void {
        this.hubSites = [];
    }

    static clearProvisionedSites(): void {
        this.provisionedSites = undefined;
    }

    static groupAndSortSites(sites: ISiteInfoLocation[]): ISiteInfoLocation[] {
        if (Array.isArray(sites)) {
            const hubSites = sites
                .filter(site => site.isHub)
                .sort(this.sortSites);

            return hubSites
                .concat(sites
                    .filter(site => !site.isHub)
                    .sort(this.sortSites));
        }
        return [];
    }

    static sortSites(siteA: ISiteInfo, siteB: ISiteInfo): number {
        return siteA.name?.toUpperCase() < siteB.name?.toUpperCase() ? -1 : 1;
    }

    static configureManagesSites(sites: ISiteInfo[]): void {
        if (Array.isArray(sites) && Array.isArray(this.managedSites?.sites) && this.managedSites.sites.length > 0) {
            for (const managedSite of this.managedSites.sites) {
                const site = sites.find(site => (
                    site?.webUrl && managedSite?.siteUrl && site.webUrl.toLowerCase().trim() === managedSite.siteUrl
                    || site?.id && managedSite?.siteId && site.id.toLowerCase().trim().indexOf(managedSite.siteId) !== -1));

                if (site) {
                    site.isManaged = true;
                    if (managedSite.siteGroup) {
                        site.managedGroup = managedSite.siteGroup;
                    }
                }
            }

            if (this.managedSites?.groups?.size > 0) {
                this.managedSites.groups.forEach(group => {
                    if (sites.some(site => site.managedGroup === group)) {
                        sites.push({
                            id: group,
                            name: group,
                            webUrl: undefined,
                            isSubsite: false,
                            isManagedGroup: true,
                            isManaged: true
                        });
                    }
                });
            }
        }
    }

    // Normalises a url for comparison: trims whitespace, lowercases, and strips trailing slashes so
    // that ".../sites/x" and ".../sites/x/" (or values with stray whitespace) still match.
    private static normalizeUrl(url?: string): string {
        let normalized: string = url?.trim()?.toLowerCase() ?? "";
        while (normalized.endsWith("/")) {
            normalized = normalized.slice(0, -1);
        }
        return normalized;
    }

    private static readonly GUID_PATTERN: RegExp = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    // The composite Graph site id is "host,sharePointSiteId,webId" and the client list's SiteId
    // column stores the MIDDLE value. useMenuItem_Open, useAggregatedCommands and sharepointInfo
    // already perform this exact extraction with a `length > 1` guard; this is the shared,
    // tested version of it. The GUID-shape check is the addition - a malformed id must decline
    // rather than build a filter out of a host name, and a BARE guid also declines because it
    // cannot be distinguished from a web id (querying with a web id would return 0 rows and
    // masquerade as "site has no client type"). The trailing web id is never returned.
    static extractSharePointSiteId(compositeId?: string): string | undefined {
        const parts: string[] = compositeId?.split(",") ?? [];
        if (parts.length < 2) {
            return undefined;
        }
        const candidate: string = parts[1]?.trim() ?? "";
        return this.GUID_PATTERN.test(candidate) ? candidate.toLowerCase() : undefined;
    }

    // Single source of truth for matching a site against a client-list entry: a normalized web
    // url, or the entry's site-collection guid appearing inside the (composite) site id. The
    // inactive-client indicator and client-group membership both go through here, so a site one
    // can flag is a site the other can list.
    static siteMatchesEntry(webUrl: string | undefined, id: string | undefined, entry: { siteUrl?: string; siteId?: string }): boolean {
        const siteUrl: string = this.normalizeUrl(webUrl);
        const siteId: string = id?.toLowerCase()?.trim();
        return (!!siteUrl && !!entry?.siteUrl && siteUrl === this.normalizeUrl(entry.siteUrl))
            || (!!siteId && !!entry?.siteId && siteId.indexOf(entry.siteId.toLowerCase().trim()) !== -1);
    }

    // Whether a loaded site IS the site a client-list entry names, for client-group membership:
    // its web url is the entry's url, or it is the ROOT of the entry's site collection. Unlike
    // siteMatchesEntry (the inactive flag, which covers a client's whole collection), a subsite
    // never stands in for its client - the client's own site is looked up instead.
    static isEntrySite(site: { webUrl?: string; id?: string; isSubsite?: boolean } | undefined, entry: { siteUrl?: string; siteId?: string }): boolean {
        const siteUrl: string = this.normalizeUrl(site?.webUrl);
        if (siteUrl && entry?.siteUrl && siteUrl === this.normalizeUrl(entry.siteUrl)) {
            return true;
        }
        const collectionId: string | undefined = this.extractSharePointSiteId(site?.id);
        return !!collectionId && !!entry?.siteId && site?.isSubsite !== true && collectionId === entry.siteId.toLowerCase().trim();
    }

    // Matches a site against the global inactive-client set.
    static isSiteInactive(webUrl?: string, id?: string): boolean {
        const inactiveEntries: IManagedSite[] = Array.isArray(this.inactiveClientSites) ? this.inactiveClientSites : [];
        if (inactiveEntries.length === 0) {
            return false;
        }
        return inactiveEntries.some(entry => this.siteMatchesEntry(webUrl, id, entry));
    }

    // Splits client-list entries into the loaded sites they name and the entries nothing loaded
    // matches (those go to Graph). A loaded site is returned at most once however many entries
    // point at it.
    static partitionLoadedSites<TEntry extends { siteUrl?: string; siteId?: string }>(
        entries: TEntry[],
        sites: ISiteInfo[] | undefined,
    ): { matched: ISiteInfo[]; unmatched: TEntry[] } {
        const matched: ISiteInfo[] = [];
        const unmatched: TEntry[] = [];
        const loaded: ISiteInfo[] = Array.isArray(sites) ? sites.filter(site => site) : [];
        const taken: Set<ISiteInfo> = new Set<ISiteInfo>();

        for (const entry of entries ?? []) {
            const site: ISiteInfo | undefined = loaded.find(candidate => this.isEntrySite(candidate, entry));
            if (!site) {
                unmatched.push(entry);
            }
            else if (!taken.has(site)) {
                taken.add(site);
                matched.push(site);
            }
        }
        return { matched, unmatched };
    }

    // Applies the global inactive-client indicator to every site (managed and normal). Sourced
    // separately from managed sites via the tenant client list; only flags sites that are loaded.
    static configureInactiveSites(sites: ISiteInfo[]): void {
        if (!Array.isArray(sites)) {
            return;
        }

        for (const site of sites) {
            if (!site) {
                continue;
            }

            // Assign explicitly (not set-true-only) so cached/re-used site objects self-heal when a
            // site becomes active again or the client list is cleared/fails to load.
            site.isInactive = this.isSiteInactive(site.webUrl, site.id);
        }
    }
}